// API client for the Submit-to-CMS attestation feature.
// Backend: api/routes/attestations.js

import userStorage from '@/lib/userStorage';

export type EvaluationType = 'GROUP' | 'NPI';

export interface CategoriesSubmitted {
  data_freeze: true;
  quality: boolean;
  ia: boolean;
  pi: boolean;
}

export interface AttestationRecord {
  id: number;
  practice_id: number;
  program_year: number;
  evaluation_type: EvaluationType;
  npi: string | null;
  clinician_name: string | null;
  entity_name: string;
  tin_last4: string;
  categories_submitted: CategoriesSubmitted;
  signer_name: string;
  signer_title_role: string;
  signed_date_asserted: string;
  authorization_text_version: string;
  signed_at: string;
  signed_by_user_id: number | null;
  voided_at?: string | null;
  voided_by_user_id?: number | null;
  voided_reason?: string | null;
  // Only present on the single-record audit view (GET /:id):
  ip_address?: string | null;
  user_agent?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AttestationsListResponse {
  latest: AttestationRecord[];
  history: AttestationRecord[];
}

export interface CreateAttestationPayload {
  program_year: number;
  evaluation_type: EvaluationType;
  npi?: string | null;
  clinician_name?: string | null;
  entity_name: string;
  tin_last4: string;
  categories_submitted: CategoriesSubmitted;
  signer_name: string;
  signer_title_role: string;
  /** ISO date string YYYY-MM-DD — the date the signer asserts on the form. */
  signed_date_asserted: string;
}

export interface CreateAttestationResponse {
  success: true;
  attestation: {
    id: number;
    signed_at: string;
    signed_date_asserted: string;
    authorization_text_version: string;
  };
}

export interface VoidAttestationResponse {
  success: true;
  attestation: {
    id: number;
    voided_at: string;
    voided_by_user_id: number | null;
    voided_reason: string;
  };
}

/** Minimal clinician shape used by the Submit to CMS NPI list (eligibility data). */
export interface SubmitCmsClinician {
  npi: string;
  clinician_name: string | null;
  clinician_type: string | null;
  mips_eligible_individual: boolean | null;
  mips_eligible_group: boolean | null;
}

// ---- Stale-while-revalidate caches for Submit to CMS ----
// The page renders instantly from these on every visit (no skeleton swap, no
// flash); a background refresh updates rows in place. Keyed `${practiceId}:${year}`.
// Cleared on logout (AuthContext) so signer/clinician names never linger across
// accounts on a shared browser.
export const submitCmsCache = {
  attestations: new Map<string, AttestationRecord[]>(),
  clinicians: new Map<string, SubmitCmsClinician[]>(),
};

export function clearSubmitCmsCaches(): void {
  submitCmsCache.attestations.clear();
  submitCmsCache.clinicians.clear();
}

const getApiUrl = () => import.meta.env.VITE_API_URL || 'http://localhost:3001';
const getAuthToken = () => localStorage.getItem('auth_token');
const getCurrentPracticeId = () => userStorage.getSessionItem('selectedPracticeId');

function authHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    return body.error || body.message || fallback;
  } catch {
    return fallback;
  }
}

export async function getAttestations(
  programYear?: number,
  practiceId?: string | number
): Promise<AttestationsListResponse> {
  const id = practiceId ?? getCurrentPracticeId();
  if (!id) throw new Error('No practice selected');

  const search = programYear ? `?program_year=${programYear}` : '';
  const url = `${getApiUrl()}/api/practices/${id}/attestations${search}`;

  const response = await fetch(url, { headers: authHeaders() });
  if (!response.ok) {
    throw new Error(await readError(response, `Failed to fetch attestations (${response.status})`));
  }
  return response.json();
}

export async function getAttestation(
  attestationId: number | string,
  practiceId?: string | number
): Promise<AttestationRecord> {
  const id = practiceId ?? getCurrentPracticeId();
  if (!id) throw new Error('No practice selected');

  const url = `${getApiUrl()}/api/practices/${id}/attestations/${attestationId}`;
  const response = await fetch(url, { headers: authHeaders() });
  if (!response.ok) {
    throw new Error(
      await readError(response, `Failed to fetch attestation (${response.status})`)
    );
  }
  return response.json();
}

export async function createAttestation(
  payload: CreateAttestationPayload,
  practiceId?: string | number
): Promise<CreateAttestationResponse> {
  const id = practiceId ?? getCurrentPracticeId();
  if (!id) throw new Error('No practice selected');

  const url = `${getApiUrl()}/api/practices/${id}/attestations`;
  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await readError(response, `Failed to record attestation (${response.status})`));
  }
  return response.json();
}

export async function voidAttestation(
  attestationId: number | string,
  reason: string,
  practiceId?: string | number
): Promise<VoidAttestationResponse> {
  const id = practiceId ?? getCurrentPracticeId();
  if (!id) throw new Error('No practice selected');

  const url = `${getApiUrl()}/api/practices/${id}/attestations/${attestationId}/void`;
  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ reason }),
  });
  if (!response.ok) {
    throw new Error(
      await readError(response, `Failed to void attestation (${response.status})`)
    );
  }
  return response.json();
}

export function formatAssertedDate(iso?: string | null): string {
  if (!iso) return '';
  // signed_date_asserted is a DATE, returned as YYYY-MM-DD. Render it as a
  // calendar date with no timezone shifting (treat as local noon to be safe).
  try {
    const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function tinLast4(tin?: string | null): string {
  if (!tin) return '';
  const digits = String(tin).replace(/\D/g, '');
  return digits.slice(-4);
}

export function formatSignedAt(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
